import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../../api/client';
import '../backlog/backlog.css';
import { AppDataGrid, type AppColumn } from '../../components/AppDataGrid';
import { AppToolbar } from '../../components/AppToolbar';
import { FormTitle } from '../../components/FormTitle';
import { type IslandStep } from '../../components/StepIsland/StepIsland';
import { TopbarStepper } from '../../components/StepIsland/TopbarStepper';
import { StepHint } from '../../components/StepHint/StepHint';
import { useConfirm } from '../../components/confirm';
import { useNotify } from '../../components/notifications/useNotify';
import { runLoading } from '../../components/loading';
import { fmtLocalDate, fmtLocalDateTime } from '../../utils/datetime';
import { DateRangeSelect, TRACKING_RANGES } from '../modules/DateRangeSelect';

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────
interface FieldDataTrackingRow {
  jobId:             string;
  jobOid:            string;
  client:            string;
  projectId:         string;
  projectName:       string;
  sampleMatrix:      string;
  status:            string;
  statusId:          string;
  samplingStatusInt: number;
  submitted:         string | null;
  validated:         string | null;
  approved:          string | null;
  lastAction:        string | null;
}

interface TrackingSummary {
  jobId:             string;
  jobOid:            string;
  client:            string;
  projectId:         string;
  projectName:       string;
  sampleMatrix:      string;
  samplingStatusInt: number;
  submittedBy:       string | null;
  submittedDate:     string | null;
  validatedBy:       string | null;
  validatedDate:     string | null;
  approvedBy:        string | null;
  approvedDate:      string | null;
  rolledBackBy:      string | null;
  rolledBackDate:    string | null;
  rollbackReason:    string | null;
}

interface StationRow {
  sampleLoginOid: string;
  stationId:      string;
  stationName:    string;
  blended:        boolean;
  latitude:       string | null;
  longitude:      string | null;
  temperature:    string | null;
  humidity:       string | null;
  stationComment: string | null;
}

interface SamplingRow {
  sampleLoginOid: string;
  stationId:      string;
  stationName:    string;
  sampleType:     string;
  sampleId:       string;
  collectorOid:   string | null;
  collectorName:  string | null;
  collectedDate:  string | null;
  sampleComment:  string;
  testSummary:    string;
  enteredBy:      string;
  enteredDate:    string | null;
}

interface MonitoringRow {
  sampleParameterOid: string;
  sampleLoginOid:     string;
  sampleId:           string;
  test:               string;
  methodName:         string;
  parameter:          string;
  result:             string;
  unit:               string;
  monitoredBy:        string;
  monitoredDate:      string | null;
  enteredBy:          string;
  enteredDate:        string | null;
}

type TabKey = 'stations' | 'sampling' | 'monitoring';

// Derives a verb ("Approved" / "Validated" / "Submitted") from whichever
// timestamp is most recent — matches the prototype's `last` field, which
// is set at each action rather than shown as a bare date.
function lastActionVerb(r: FieldDataTrackingRow): string {
  if (r.approved)  return 'Approved';
  if (r.validated) return 'Validated';
  if (r.submitted) return 'Submitted';
  return '—';
}

type DateRangeKey = '1M' | '3M' | '6M' | '1Y' | 'All';

function statusPillVariant(statusId: string): string {
  if (statusId === 'FD-2-6') return 'ok';
  if (statusId === 'FD-2-5') return 'info';
  if (statusId === 'FD-2-3') return 'warn';
  return 'info';
}

function summaryField(by: string | null, date: string | null): string {
  if (!by && !date) return '—';
  if (by && date) return `${by} · ${fmtLocalDateTime(date)}`;
  return by ?? (date ? fmtLocalDateTime(date) : '—');
}

// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────
export default function FieldDataTrackingPage() {
  // ── List state ──────────────────────────────────────────────────────────────
  const [rows,        setRows]        = useState<FieldDataTrackingRow[]>([]);
  const [loading,     setLoading]     = useState(true);
  const [loadMs,      setLoadMs]      = useState<number | null>(null);
  const [error,       setError]       = useState<string | null>(null);
  const [dateRange,   setDateRange]   = useState<DateRangeKey>('3M');
  const [selectedIds, setSelectedIds] = useState<Set<string | number>>(new Set());

  // ── Detail state ────────────────────────────────────────────────────────────
  const [view,          setView]          = useState<'list' | 'detail'>('list');
  const [selectedJob,   setSelectedJob]   = useState<FieldDataTrackingRow | null>(null);
  const [summary,       setSummary]       = useState<TrackingSummary | null>(null);
  const [activeTab,     setActiveTab]     = useState<TabKey>('stations');
  const [stations,      setStations]      = useState<StationRow[]>([]);
  const [sampling,      setSampling]      = useState<SamplingRow[]>([]);
  const [monitoring,    setMonitoring]    = useState<MonitoringRow[]>([]);

  // Row-selection checkboxes on all three detail tabs — GoLive parity (Field Data
  // Tracking opens the SAME Samplecheckin_DetailView_FieldDataReview1 as Field Data
  // Review 1, which carries the tick column on each tab). The Sampling Information
  // ticks DRIVE the detail Rollback (RollBack_Accepting is per selected sample).
  const [selStationIds,    setSelStationIds]    = useState<Set<string | number>>(new Set());
  const [selSamplingIds,   setSelSamplingIds]   = useState<Set<string | number>>(new Set());
  const [selMonitoringIds, setSelMonitoringIds] = useState<Set<string | number>>(new Set());

  const [detailLoading, setDetailLoading] = useState(false);
  const [detailLoadMs,  setDetailLoadMs]  = useState<number | null>(null);
  // Permission gate ported from GoLive FieldDataReviewController.OnActivated —
  // Rollback is enabled only for admins or roles with Write=true on the
  // FieldDataTracking navigation permission.
  const [canWrite, setCanWrite] = useState(true);

  const notify                = useNotify();
  const { prompt: promptDlg } = useConfirm();
  const detailT0 = useRef<number>(0);

  // ── Load list ──────────────────────────────────────────────────────────────
  const loadList = useCallback(async () => {
    setLoading(true);
    setError(null);
    const t0 = performance.now();
    try {
      // The date cutoff is computed on the SERVER (from its own clock), never
      // here — sending a browser-computed date let the client's timezone
      // drift a day away from the server's and silently drop/add a whole
      // day of rows at the boundary. Just send which range is selected.
      const params: Record<string, string> = {};
      if (dateRange !== 'All') params.range = dateRange;
      const res = await api.get('/api/v1/field-data-tracking', { params });
      setRows((res.data as { data: FieldDataTrackingRow[] }).data ?? []);
      setSelectedIds(new Set());
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load');
    } finally {
      setLoadMs(performance.now() - t0);
      setLoading(false);
    }
  }, [dateRange]);

  useEffect(() => { void loadList(); }, [loadList]);

  // Field Data Tracking lists ONE ROW PER (job, sampling status) — a job appears
  // once for each stage it has reached (e.g. 260804006 shows both FD-2-1 and
  // FD-2-6). The grid row id must therefore include the status: keying on jobOid
  // alone gave the two rows the SAME id, so ticking one visually ticked both
  // (user report 2026-08-03). Resolve a row back to its jobOid via `rows` before
  // calling any API — never treat this composite id as a jobOid.
  const trackRowId = (r: FieldDataTrackingRow) => `${r.jobOid}:${r.samplingStatusInt}`;

  // ── Single-select (radio-button style) — only one job may be ticked at a
  // time, since Load/Rollback operate on one record here. Header "select all"
  // collapses to the last row rather than checking every row.
  const handleSelectionChange = useCallback((next: Set<string | number>) => {
    if (next.size <= 1) { setSelectedIds(next); return; }
    const newlyAdded = [...next].find(id => !selectedIds.has(id));
    setSelectedIds(new Set([newlyAdded ?? [...next][next.size - 1]!]));
  }, [selectedIds]);

  // ── Permission gate (ported from GoLive FieldDataReviewController.OnActivated) ──
  useEffect(() => {
    api.get('/api/v1/field-data-tracking/permissions')
      .then(res => setCanWrite(Boolean((res.data as { data: { canWrite: boolean } }).data?.canWrite)))
      .catch(() => setCanWrite(false));
  }, []);

  // ── Load detail (after clicking Load) ──────────────────────────────────────
  const openDetail = useCallback(async (job: FieldDataTrackingRow) => {
    setSelectedJob(job);
    setDetailLoading(true);
    setActiveTab('stations');
    // Clear per-tab ticks so one job's selection never carries into the next.
    setSelStationIds(new Set()); setSelSamplingIds(new Set()); setSelMonitoringIds(new Set());
    detailT0.current = performance.now();
    try {
      // Scope every tab to the CLICKED ROW's specific status — a job can span
      // multiple statuses in Tracking (one row per status), and GoLive's
      // detail view (strMode=="View") shows only that status's samples, not
      // the whole job.
      const statusParams = { params: { status: job.samplingStatusInt } };
      const [sm, st, sp, mn] = await Promise.all([
        api.get(`/api/v1/field-data-tracking/${job.jobOid}/summary`),
        api.get(`/api/v1/field-data-tracking/${job.jobOid}/stations`, statusParams),
        api.get(`/api/v1/field-data-tracking/${job.jobOid}/sampling`, statusParams),
        api.get(`/api/v1/field-data-tracking/${job.jobOid}/monitoring`, statusParams),
      ]);
      setSummary((sm.data as { data: TrackingSummary | null }).data ?? null);
      setStations((st.data as { data: StationRow[] }).data ?? []);
      setSampling((sp.data as { data: SamplingRow[] }).data ?? []);
      setMonitoring((mn.data as { data: MonitoringRow[] }).data ?? []);
      setDetailLoadMs(performance.now() - detailT0.current);
      setView('detail');
    } catch {
      notify.error('Failed to load job detail. Please try again.');
    } finally {
      setDetailLoading(false);
    }
  }, [notify]);

  const closeDetail = useCallback(() => {
    setView('list');
    setSelectedJob(null);
    setSummary(null);
    setStations([]); setSampling([]); setMonitoring([]);
    setSelStationIds(new Set()); setSelSamplingIds(new Set()); setSelMonitoringIds(new Set());
    void loadList();
  }, [loadList]);

  // ── Load (list view) ────────────────────────────────────────────────────────
  const handleLoad = useCallback(async () => {
    if (selectedIds.size !== 1) { notify.error('Select exactly one job to load.'); return; }
    const job = rows.find(r => selectedIds.has(trackRowId(r)));
    if (job) void openDetail(job);
  }, [selectedIds, rows, notify, openDetail]);

  // ── Rollback ─────────────────────────────────────────────────────────────────
  // TWO scopes, matching GoLive FieldDataReviewController exactly:
  //   • LIST view  → RollBackListView_Accepting: WHOLE JOB (loops every SampleLogIn
  //     of the selected Samplecheckin). selectedSampleOids omitted.
  //   • DETAIL view → RollBack_Accepting: ONLY the samples ticked on the Sampling
  //     Information tab (Field Data Tracking opens the same
  //     Samplecheckin_DetailView_FieldDataReview1, audited as "Field Data Tracking").
  // FIXED 2026-08-03 (user report): the detail action previously sent no sample oids,
  // so the server fell back to the whole job.
  const rollbackJobs = useCallback(async (
    jobOids: string[], selectedSampleOids?: string[], scopeLabel?: string,
  ) => {
    if (jobOids.length === 0) { notify.error('Select at least one job to roll back.'); return; }
    if (!canWrite) { notify.error('You do not have permission to roll back field data.'); return; }
    const count = jobOids.length;
    const reason = await promptDlg({
      title: 'Rollback Reason',
      message: scopeLabel ?? `This rolls back ALL samples in ${count} job${count > 1 ? 's' : ''}. `
             + `Enter reason for rolling back:`,
      required: true,
    });
    if (!reason) return;

    let failed = 0;
    await runLoading(
      Promise.all(jobOids.map(jobOid =>
        api.post(`/api/v1/field-data-tracking/${jobOid}/rollback`,
          selectedSampleOids && selectedSampleOids.length > 0
            ? { reason, selectedSampleOids }
            : { reason })
          .catch(() => { failed++; })
      )),
      { blocking: true, label: 'Rolling back…' }
    );
    if (failed > 0) {
      // GoLive shows the error and STAYS on the current view — Frame.SetView(list)
      // sits at the END of RollBack_Accepting's try block, so it is only reached on
      // success. Staying put also lets the user retry without re-selecting.
      notify.error(`${failed} job${failed > 1 ? 's' : ''} failed to roll back.`);
      return;
    }
    notify.success('Rolled back successfully.');
    // ALWAYS return to the LIST view after a successful rollback — GoLive
    // RollBack_Accepting / RollBackListView_Accepting both end with
    //   CreateListView = Application.CreateListView("SampleLogIn_ListView_FieldDataTracking", …);
    //   Frame.SetView(CreateListView);
    // (user request 2026-08-03). Unconditional on purpose: keying this off the
    // `view` state risked a stale closure leaving the user on the detail page.
    // closeDetail() resets the detail state, clears the tab ticks and reloads the
    // list, so the rolled-back job shows its new status immediately.
    closeDetail();
  }, [canWrite, promptDlg, notify, closeDetail]);

  // Resolve the ticked ROW ids back to real jobOids — the grid id is
  // `${jobOid}:${samplingStatusInt}`, so it must never be sent to the API as-is.
  const handleListRollback = useCallback(() => {
    const jobOids = [...new Set(
      rows.filter(r => selectedIds.has(trackRowId(r))).map(r => r.jobOid))];
    void rollbackJobs(jobOids);
  }, [rows, selectedIds, rollbackJobs]);

  // Detail rollback — GoLive gate: at least one sample must be ticked.
  const handleDetailRollback = useCallback(() => {
    if (!selectedJob) return;
    const picked = sampling
      .filter(r => selSamplingIds.has(r.sampleLoginOid))
      .map(r => r.sampleLoginOid);
    if (picked.length === 0) {
      notify.error('Select atleast one Sample to rollback.');
      setActiveTab('sampling');
      return;
    }
    void rollbackJobs([selectedJob.jobOid], picked,
      `Enter reason for rolling back ${picked.length} selected sample${picked.length > 1 ? 's' : ''} `
      + `in job ${selectedJob.jobId}:`);
  }, [selectedJob, sampling, selSamplingIds, notify, rollbackJobs]);

  // ── Grid columns — match prototype exactly ─────────────────────────────────
  const cols: AppColumn<FieldDataTrackingRow>[] = [
    { field: 'jobId',     header: 'JobID',     width: 130 },
    { field: 'client',    header: 'Client',    width: 180 },
    { field: 'projectId', header: 'ProjectID', width: 130 },
    { field: 'status',    header: 'Status',    width: 200,
      text: r => r.status,
      render: r => <span className={`bl-pill ${statusPillVariant(r.statusId)}`}>{r.status}</span> },
    { field: 'statusId',  header: 'StatusID',  width: 90 },
    { field: 'submitted', header: 'Submitted', width: 130, type: 'date',
      render: r => <span>{fmtLocalDate(r.submitted)}</span> },
    { field: 'validated', header: 'Validated', width: 130, type: 'date',
      render: r => <span>{fmtLocalDate(r.validated)}</span> },
    { field: 'approved',  header: 'Approved',  width: 130, type: 'date',
      render: r => <span>{fmtLocalDate(r.approved)}</span> },
    { field: 'lastAction', header: 'Last Action', width: 130,
      text: r => lastActionVerb(r),
      render: r => <span>{lastActionVerb(r)}</span> },
  ];

  // ── Station grid columns (read-only) ────────────────────────────────────────
  const stationCols: AppColumn<StationRow>[] = [
    { field: 'stationId',      header: 'JobID',             width: 120 },
    { field: 'stationName',    header: 'StationLocation',   width: 160 },
    { field: 'blended',        header: 'Blended',           width: 90, align: 'center',
      render: r => (
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <input type="checkbox" checked={r.blended} readOnly
            style={{ cursor: 'default', width: 14, height: 14 }} />
        </div>
      ) },
    { field: 'latitude',       header: 'Latitude',          width: 120 },
    { field: 'longitude',      header: 'Longitude',         width: 120 },
    { field: 'temperature',    header: 'Temperature (°F)',  width: 140 },
    { field: 'humidity',       header: 'Humidity (%)',      width: 120 },
    { field: 'stationComment', header: 'StationComment',    width: 200 },
  ];

  // ── Sampling grid columns (read-only) ───────────────────────────────────────
  const samplingCols: AppColumn<SamplingRow>[] = [
    { field: 'stationName',     header: 'StationLocation',  width: 150 },
    { field: 'sampleId',        header: 'SampleID',         width: 140 },
    { field: 'collectorName',   header: 'CollectedBy',      width: 140 },
    { field: 'collectedDate',   header: 'CollectedDate',    width: 160, type: 'datetime',
      render: r => <span>{r.collectedDate ? fmtLocalDateTime(r.collectedDate) : ''}</span> },
    { field: 'testSummary',     header: 'TestSummary',      width: 340 },
    { field: 'sampleComment',   header: 'SampleComment',    width: 180 },
    { field: 'enteredBy',       header: 'EnteredBy',        width: 120 },
    { field: 'enteredDate',     header: 'EnteredDate',      width: 160, type: 'datetime',
      render: r => <span>{r.enteredDate ? fmtLocalDateTime(r.enteredDate) : ''}</span> },
  ];

  // ── Monitoring grid columns (read-only) ─────────────────────────────────────
  const monitoringCols: AppColumn<MonitoringRow>[] = [
    { field: 'sampleId',    header: 'SampleID',       width: 140 },
    { field: 'test',        header: 'Test',           width: 180 },
    { field: 'methodName',  header: 'MethodName',     width: 140 },
    { field: 'parameter',   header: 'Parameter',      width: 180 },
    { field: 'result',      header: 'Result',         width: 90, align: 'right' },
    { field: 'unit',        header: 'Units',          width: 110 },
    { field: 'monitoredBy', header: 'MonitoredBy',    width: 130 },
    { field: 'monitoredDate', header: 'MonitoredDate', width: 160, type: 'datetime',
      render: r => <span>{r.monitoredDate ? fmtLocalDateTime(r.monitoredDate) : ''}</span> },
    { field: 'enteredBy',   header: 'EnteredBy',      width: 120 },
    { field: 'enteredDate', header: 'EnteredDate',    width: 160, type: 'datetime',
      render: r => <span>{r.enteredDate ? fmtLocalDateTime(r.enteredDate) : ''}</span> },
  ];

  const loadedJob = selectedIds.size === 1
    ? rows.find(r => selectedIds.has(trackRowId(r))) ?? null
    : null;

  // ── Island stepper — Select Job → Entry → Validation → Approval ────────────
  const islandSteps = useMemo((): IslandStep[] => {
    const s = view === 'detail' ? summary : null;
    const jobDone = view === 'detail' ? true : loadedJob != null;
    const entryDone = !!s?.submittedDate || (s?.samplingStatusInt ?? -1) >= 1;
    const validationDone = !!s?.validatedDate || (s?.samplingStatusInt ?? -1) >= 2;
    const approvalDone = !!s?.approvedDate || (s?.samplingStatusInt ?? -1) >= 3;
    return [
      { id: 'job',        label: 'Select Job',  state: jobDone ? 'done' : 'active' },
      { id: 'entry',      label: 'Entry',       state: !jobDone ? 'todo' : entryDone ? 'done' : 'active' },
      { id: 'validation', label: 'Validation',  state: !jobDone ? 'todo' : validationDone ? 'done' : entryDone ? 'active' : 'todo' },
      { id: 'approval',   label: 'Approval',    state: !jobDone ? 'todo' : approvalDone ? 'done' : validationDone ? 'active' : 'todo' },
    ];
  }, [view, loadedJob, summary]);

  // ── Footer item count label (shows date range hint) ────────────────────────
  const rangeLabel = dateRange === 'All' ? '' : ` · last ${dateRange === '1M' ? '1 month' : dateRange === '3M' ? '3 months' : dateRange === '6M' ? '6 months' : '1 year'}`;

  // ═══════════════════════════════════════════════════════════════════
  // LIST VIEW
  // ═══════════════════════════════════════════════════════════════════
  if (view === 'list') {
    return (
      <div className="bl-page">
        <TopbarStepper steps={islandSteps} />

        <div className="bl-top">
          <div className="dp-page-hdr">
            <div className="dp-breadcrumb">
              <span>Home</span>
              <i className="ti ti-chevron-right" />
              <span>Sampling Management</span>
              <i className="ti ti-chevron-right" />
              <span className="dp-bc-active">Field Data Tracking</span>
            </div>

            <div className="dp-title-row" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <FormTitle pageId="fielddata" auditFormName="Field Data Tracking"
                loadMs={loadMs} loading={loading} />
              <div style={{ display: 'flex', gap: 6 }}>
                <button
                  className="ca-btn ca-btn-solid"
                  disabled={selectedIds.size !== 1}
                  onClick={() => void handleLoad()}
                  title={selectedIds.size !== 1 ? 'Tick a Job ID to load' : `Open ${loadedJob?.jobId}`}
                >
                  <span aria-hidden="true">⬇️</span>&nbsp;Load
                </button>
                {/* LIST-view rollback = WHOLE JOB (GoLive RollBackListView_Accepting).
                    The DETAIL-view Rollback is scoped to the ticked samples — labelled
                    apart so the two are never confused (see Field Data Validation). */}
                <button
                  className="ca-btn"
                  disabled={selectedIds.size === 0 || !canWrite}
                  title={!canWrite
                    ? 'You do not have permission to roll back field data'
                    : 'Rolls back EVERY sample in the job. To roll back only some samples, click Load and use Rollback there.'}
                  onClick={() => void handleListRollback()}
                >
                  <span aria-hidden="true">↩️</span>&nbsp;Rollback Job
                </button>
              </div>
            </div>

            <div className="dp-subtitle">
              Every job in the field-data pipeline with who did what, when. Jobs appear here from their first <strong>Submit</strong>. Tick a Job ID and click <strong>Load</strong> to open the full record —
              then tick individual samples there to roll back just those. <strong>Rollback Job</strong> here rolls back <em>every</em> sample in the job.
            </div>

            <StepHint tone="info">
              <b>Tracking.</b> A job shows here the moment it is first submitted, then follows <b>FD-2-3 → FD-2-4 → FD-2-5</b>. <b>Load</b> opens the full record; <b>Rollback</b> (mandatory reason, audited) returns any in-pipeline job to <b>Pending Submission (FD-2-2)</b>.
            </StepHint>
          </div>
        </div>

        <div className="bl-grid-area">
          {/* ── Toolbar: date chips left · Refresh · Export to right ── */}
          <div style={{ margin: '0 0 8px' }}>
            <AppToolbar
              left={
                <DateRangeSelect
                  variant="dropdown"
                  label="Date Range"
                  ranges={TRACKING_RANGES}
                  value={TRACKING_RANGES.find(r => r.label === dateRange)?.days ?? null}
                  onChange={(d) => setDateRange((TRACKING_RANGES.find(r => r.days === d)?.label ?? 'All') as DateRangeKey)}
                />
              }
              onRefresh={() => void loadList()}
              refreshing={loading}
              onExport={() => notify.info('Export started.')}
            />
          </div>

          {/* ── Grid ── */}
          <div className="bl-card bl-card-flex">
            {error ? (
              <div className="bl-err" style={{ margin: 12 }}>
                <i className="ti ti-alert-circle" /> {error}
                <button onClick={() => void loadList()}
                  style={{ marginLeft: 10, color: 'var(--brand-accent,#e07b39)', border: 'none', background: 'none', cursor: 'pointer', textDecoration: 'underline' }}>
                  Retry
                </button>
              </div>
            ) : (
              <AppDataGrid<FieldDataTrackingRow>
                rows={rows}
                columns={cols}
                getRowId={trackRowId}
                loading={loading}
                selectable
                selectedIds={selectedIds}
                onSelectionChange={handleSelectionChange}
                emptyText={`No tracking data yet${rangeLabel} — a job appears here the moment it is first submitted from Field Data Entry.`}
                onRowDoubleClick={r => void openDetail(r)}
              />
            )}
          </div>
        </div>
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════════
  // DETAIL VIEW
  // ═══════════════════════════════════════════════════════════════════
  return (
    <div className="bl-page">
      <TopbarStepper steps={islandSteps} />

      <div className="bl-top">
        <div className="dp-page-hdr">
          <div className="dp-breadcrumb">
            <span
              style={{ color: 'var(--brand-accent,#e07b39)', cursor: 'pointer', textDecoration: 'underline' }}
              onClick={closeDetail}
            >Field Data Tracking</span>
            <i className="ti ti-chevron-right" />
            <span className="dp-bc-active">{selectedJob?.jobId}</span>
          </div>

          <div className="dp-title-row" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <FormTitle
              pageId="fielddata"
              title={`Field Data Tracking — ${selectedJob?.jobId ?? ''}`}
              auditFormName="Field Data Tracking"
              loadMs={detailLoadMs}
              loading={detailLoading}
            />
            <button
              className="ca-btn"
              disabled={!canWrite}
              title={!canWrite
                ? 'You do not have permission to roll back field data'
                : selSamplingIds.size === 0
                  ? 'Tick the samples to roll back on the Sampling Information tab'
                  : `Roll back ${selSamplingIds.size} selected sample(s)`}
              onClick={() => void handleDetailRollback()}
            >
              <span aria-hidden="true">↩️</span>&nbsp;Rollback
            </button>
          </div>

          <StepHint tone="info">
            <b>Tracking · read-only.</b> The complete record across all three processes — workflow summary above, the Entry data (Stations / Sampling / Monitoring) in the tabs below.
          </StepHint>
        </div>
      </div>

      {/* ── Workflow Summary ── */}
      <div style={{ padding: '0 20px 10px' }}>
        <div className="bl-card" style={{ padding: '10px 16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, fontWeight: 600, fontSize: 12.5 }}>
            <span style={{ width: 3, height: 14, background: 'var(--brand-accent,#e07b39)', display: 'inline-block', borderRadius: 2 }} />
            Workflow Summary — Entry · Validation · Approval
            {selectedJob && <span className={`bl-pill ${statusPillVariant(selectedJob.statusId)}`} style={{ marginLeft: 4 }}>{selectedJob.status}</span>}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px 24px', fontSize: 12 }}>
            <div><label style={{ color: 'var(--ai-muted)', display: 'block', marginBottom: 2 }}>Submitted</label>
              <strong>{summaryField(summary?.submittedBy ?? null, summary?.submittedDate ?? null)}</strong></div>
            <div><label style={{ color: 'var(--ai-muted)', display: 'block', marginBottom: 2 }}>Validated</label>
              <strong>{summaryField(summary?.validatedBy ?? null, summary?.validatedDate ?? null)}</strong></div>
            <div><label style={{ color: 'var(--ai-muted)', display: 'block', marginBottom: 2 }}>Approved</label>
              <strong>{summaryField(summary?.approvedBy ?? null, summary?.approvedDate ?? null)}</strong></div>
            <div><label style={{ color: 'var(--ai-muted)', display: 'block', marginBottom: 2 }}>Rolled Back</label>
              <strong>{summaryField(summary?.rolledBackBy ?? null, summary?.rolledBackDate ?? null)}</strong></div>
            <div><label style={{ color: 'var(--ai-muted)', display: 'block', marginBottom: 2 }}>Rollback Reason</label>
              <strong>{summary?.rollbackReason || '—'}</strong></div>
            <div><label style={{ color: 'var(--ai-muted)', display: 'block', marginBottom: 2 }}>Last Action</label>
              <strong>{selectedJob ? lastActionVerb(selectedJob) : '—'}</strong></div>
            <div><label style={{ color: 'var(--ai-muted)', display: 'block', marginBottom: 2 }}>StatusID</label>
              <strong>{selectedJob?.statusId ?? '—'}</strong></div>
          </div>
        </div>
      </div>

      {/* ── Tabs ── */}
      <div className="bl-grid-area">
        <div className="sa-filter-row" style={{ marginBottom: 8 }}>
          {(['stations', 'sampling', 'monitoring'] as TabKey[]).map(tab => {
            const labels: Record<TabKey, string> = {
              stations:   'Station Information',
              sampling:   'Sampling Information',
              monitoring: 'Monitoring Records',
            };
            return (
              <button
                key={tab}
                className={`sa-filter-btn${activeTab === tab ? ' active' : ''}`}
                onClick={() => setActiveTab(tab)}
              >
                {labels[tab]}
              </button>
            );
          })}
        </div>

        {/* Read-only notice + toolbar */}
        <div style={{ margin: '8px 0', display: 'flex', alignItems: 'center', gap: 8 }}>
          <AppToolbar
            onRefresh={() => {
              if (selectedJob) void openDetail(selectedJob);
            }}
            refreshing={detailLoading}
            onExport={() => notify.info('Export started.')}
          />
          <span style={{ fontSize: 11, color: 'var(--ai-muted)', marginLeft: 4 }}>Read-only review.</span>
        </div>

        {/* ── Station Information tab ── */}
        {activeTab === 'stations' && (
          <div className="bl-card bl-card-flex">
            <AppDataGrid<StationRow>
              rows={stations}
              columns={stationCols}
              getRowId={r => r.sampleLoginOid}
              loading={detailLoading}
              emptyText="No station rows."
              selectable
              selectedIds={selStationIds}
              onSelectionChange={setSelStationIds}
            />
          </div>
        )}

        {/* ── Sampling Information tab ── */}
        {activeTab === 'sampling' && (
          <div className="bl-card bl-card-flex">
            <AppDataGrid<SamplingRow>
              rows={sampling}
              columns={samplingCols}
              getRowId={r => r.sampleLoginOid}
              loading={detailLoading}
              emptyText="No sampling rows."
              selectable
              selectedIds={selSamplingIds}
              onSelectionChange={setSelSamplingIds}
            />
          </div>
        )}

        {/* ── Monitoring Records tab ── */}
        {activeTab === 'monitoring' && (
          <div className="bl-card bl-card-flex">
            <AppDataGrid<MonitoringRow>
              rows={monitoring}
              columns={monitoringCols}
              getRowId={r => r.sampleParameterOid}
              loading={detailLoading}
              emptyText="No monitoring rows."
              selectable
              selectedIds={selMonitoringIds}
              onSelectionChange={setSelMonitoringIds}
            />
          </div>
        )}
      </div>
    </div>
  );
}
